// The 📦 Schowek panel (spec 2026-09-29-farm-4a-objects-design.md §2): everything bought or
// stored away but not on the farm, with counts; a tap closes it and starts placing that item.
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { lockInput, unlockInput } from '../ui/inputLock.js';
import { trackPanel } from '../ui/panelSwitch.js';
import { ensureLoaded } from '../ui/sceneLoading.js';
import { storageItems, storagePage } from './farmStorage.mjs';
import { gamePanel, gameButton, gameCard, woodTitle, pageArrows } from '../ui/gameUi.js';
import { iconText } from '../ui/iconText.js';
import { objectTextureKey } from './farmObjects.mjs';

const FONT = 'system-ui, -apple-system, sans-serif';
const COLS = 4, CARD_W = 200, CARD_H = 170, GAP = 14;
const texKey = (item) => objectTextureKey(item, gameState.raw?.catalog?.farm?.objects);   // a tree: its ripe look
const PH = { hut: 'farm_obj_ph_landmark', tree: 'farm_obj_ph_tree', landmark: 'farm_obj_ph_landmark', hay: 'farm_obj_ph_hay', plaque: 'farm_obj_ph_plaque', fence: 'farm_obj_ph_fence_rail' };

export async function openFarmStorage(scene, { onPick } = {}) {
  lockInput(scene);
  const items = storageItems(gameState.raw);
  await ensureLoaded(scene, items.map((i) => texKey(i.item))).catch(() => {});
  const layer = scene.add.container(0, 0).setDepth(2000).setScrollFactor(0);
  let closed = false;
  const close = (pick = null) => { if (closed) return; closed = true; untrack(); unlockInput(scene); layer.destroy(true); if (pick) onPick?.(pick); };
  const untrack = trackPanel(scene, 'storage', () => close());
  const overlay = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
  overlay.on('pointerup', () => close());
  const panelHit = scene.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 1040, 560, 0xffffff, 0.001).setInteractive();
  const panel = gamePanel(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2, 1040, 560);
  const title = woodTitle(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 - 276, '{crate} Schowek', { fontSize: 24 });
  layer.add([overlay, panelHit, panel, title]);
  if (!items.length) layer.add(iconText(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Schowek jest pusty', { fontFamily: FONT, fontSize: '20px', color: '#555' }).setOrigin(0.5));
  const x0 = GAME_WIDTH / 2 - (COLS * CARD_W + (COLS - 1) * GAP) / 2 + CARD_W / 2;
  let page = 0, body = null;
  const draw = () => {
  body?.destroy(true);
  body = scene.add.container(0, 0);
  layer.add(body);
  const view = storagePage(items, page);
  page = view.page;
  view.items.forEach((it, i) => {
    const cx = x0 + (i % COLS) * (CARD_W + GAP), cy = GAME_HEIGHT / 2 - 110 + Math.floor(i / COLS) * (CARD_H + GAP);
    const waiting = it.kind === 'waiting';                // 4c: animals waiting for a hut — shown, not placed
    const card = gameCard(scene, cx, cy, CARD_W, CARD_H);
    if (!waiting) card.setInteractive({ useHandCursor: true });
    const sp = waiting ? it.item.slice(5) : null;
    const key = waiting ? ([`farm_animal_${sp}_idle`, `farm_animal_ph_${sp}`].find((k) => scene.textures.exists(k)) ?? 'farm_obj_ph_decoration')
      : scene.textures.exists(texKey(it.item)) ? texKey(it.item) : (PH[it.kind] ?? 'farm_obj_ph_decoration');
    const img = scene.add.image(cx, cy - 25, key);
    const src = img.texture.getSourceImage();
    const k = Math.min(120 / src.width, 90 / src.height); img.setDisplaySize(src.width * k, src.height * k);
    const name = iconText(scene, cx, cy + 42, it.name, { fontFamily: FONT, fontSize: '15px', color: '#000', fontStyle: 'bold', align: 'center', wordWrap: { width: CARD_W - 16 } }).setOrigin(0.5);
    const count = iconText(scene, cx + CARD_W / 2 - 12, cy - CARD_H / 2 + 10, `× ${it.count}`, { fontFamily: FONT, fontSize: '16px', color: '#b8860b', fontStyle: 'bold' }).setOrigin(1, 0);
    if (!waiting) card.on('pointerup', () => close(it.item));
    body.add([card, img, name, count]);
    if (waiting) {
      const hut = gameState.raw?.catalog?.farm?.animals?.[sp]?.hut;
      const hutName = gameState.raw?.catalog?.farm?.objects?.[hut]?.name_pl?.toLowerCase() ?? 'domek';
      body.add(iconText(scene, cx, cy + 64, `postaw ${hutName} — wyjdą`, { fontFamily: FONT, fontSize: '13px', color: '#777' }).setOrigin(0.5));
    }
  });
  if (view.pages > 1) {                                      // 8 per page: nothing becomes unreachable
    const arrowDx = (COLS * CARD_W + (COLS - 1) * GAP) / 2 + 52;   // round wooden arrows, like the market
    body.add(pageArrows(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 - 18, arrowDx, page, view.pages, (p) => { page = p; draw(); }));
    body.add(iconText(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 200, `${page + 1} / ${view.pages}`, { fontFamily: FONT, fontSize: '15px', color: '#555' }).setOrigin(0.5));
  }
  };
  draw();
  layer.add(gameButton(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 235, 200, 46, 'Zamknij', () => close(), { variant: 'wood', fontSize: 18 }));
}
